import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { ShopPromotionListQueryDTO } from "@/modules/merchant/domain/contracts";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const PROMOTIONS_ROUTE = "/app/shops/$shopId/promotions" as const;

export type ShopPromotionsListSearch = {
  offset: number;
  limit: number;
  search?: string;
  status: ShopPromotionListQueryDTO["status"];
};

const STATUS_OPTIONS: {
  value: ShopPromotionsListSearch["status"];
  label: string;
}[] = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "active", label: "ໃຊ້ແລ້ວ" },
  { value: "paused", label: "ຢຸດຊົ່ວຄາວ" },
];

export function ShopPromotionsFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: PROMOTIONS_ROUTE });
  const search = (useSearch({
    from: PROMOTIONS_ROUTE,
  }) ?? {}) as ShopPromotionsListSearch;

  const [qInput, setQInput] = useState(search.search ?? "");

  useEffect(() => {
    setQInput(search.search ?? "");
  }, [search.search]);

  const debounced = useDebounceCallback((val: string) => {
    if (!shopId) return;
    const q = val.trim() !== "" ? val.trim().slice(0, 200) : undefined;
    nav({
      to: PROMOTIONS_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        status: search.status,
        search: q,
      },
    });
  }, 400);

  const go = (next: Partial<ShopPromotionsListSearch>) => {
    if (!shopId) return;
    nav({
      to: PROMOTIONS_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: next.limit ?? search.limit ?? 20,
        status: next.status ?? search.status,
        search: next.search !== undefined ? next.search : search.search,
      },
    });
  };

  const hasFilter = Boolean(search.search?.trim()) || search.status !== "all";

  if (!shopId) return null;

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="flex max-sm:flex-col items-center justify-between gap-4">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ (ຊື່ໂປຣ)…"
            value={qInput}
            onChange={(e) => {
              const v = e.target.value;
              setQInput(v);
              debounced(v);
            }}
            className="h-8 w-full sm:max-w-xs"
          />
          <div className="flex w-full flex-col gap-1 sm:w-44">
            <span className="sr-only">ສະຖານະ</span>
            <SimpleSelect
              value={search.status}
              onValueChange={(v) => {
                go({
                  status: v as ShopPromotionsListSearch["status"],
                });
              }}
              options={STATUS_OPTIONS}
              placeholder="ສະຖານະ"
              className="h-8 w-full"
            />
          </div>
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() =>
              nav({
                to: PROMOTIONS_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  status: "all",
                  search: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
